import type { ChartPeriod, ChartDimensions, GroupedMetric, TooltipData } from '../../types';
import { CHART_CONSTANTS } from '../../constants';
import { formatCompactCurrency } from '../../utils';

interface BaseBarSeriesProps {
  /** Callback when hovering over a bar */
  onHover?: (data: TooltipData | null) => void;
}

interface StandardBarSeriesProps extends BaseBarSeriesProps {
  mode: 'standard';
  periods: ChartPeriod[];
  dimensions: ChartDimensions;
  currentTotals: Map<string, number>;
  comparisonTotals: Map<string, number>;
  hasComparison: boolean;
  maxValue: number;
}

interface MetricBarSeriesProps extends BaseBarSeriesProps {
  mode: 'metric';
  periods: ChartPeriod[];
  dimensions: ChartDimensions;
  allGroupedMetrics: GroupedMetric[];
}

type BarSeriesProps = StandardBarSeriesProps | MetricBarSeriesProps;

/**
 * Renders bar chart series.
 * Supports standard mode (current vs comparison) and metric mode (multiple metrics).
 * Includes hover interaction for tooltip display.
 */
export function BarSeries(props: BarSeriesProps) {
  const { periods, dimensions, onHover } = props;
  const { getX, paddingTop, chartHeight, barWidth } = dimensions;
  const { COLORS } = CHART_CONSTANTS;
  const barBottom = paddingTop + chartHeight;

  if (props.mode === 'metric') {
    const { allGroupedMetrics } = props;

    return (
      <>
        {allGroupedMetrics.map((metric, metricIndex) => (
          periods.map((p, i) => {
            const x = getX(i);
            const value = metric.values.get(p.key) || 0;
            const barHeight = Math.max(0, (value / metric.groupMaxValue) * chartHeight);
            const seriesBarWidth = Math.max(8, barWidth);
            const totalWidth = allGroupedMetrics.length * seriesBarWidth + (allGroupedMetrics.length - 1) * 4;
            const barX = x - totalWidth / 2 + metricIndex * (seriesBarWidth + 4);
            const barY = barBottom - barHeight;

            const handleMouseEnter = () => {
              onHover?.({
                x: barX + seriesBarWidth / 2,
                y: barY - 8,
                periodKey: p.key,
                periodLabel: p.label,
                currentValue: value,
                metricKey: metric.key,
                isCurrency: metric.isCurrency,
              });
            };

            const handleMouseLeave = () => {
              onHover?.(null);
            };

            return (
              <g key={`${metric.key}-${p.key}`}>
                <rect
                  x={barX}
                  y={barY}
                  width={seriesBarWidth}
                  height={barHeight}
                  fill={metric.color}
                  rx="2"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onFocus={handleMouseEnter}
                  onBlur={handleMouseLeave}
                  tabIndex={0}
                  role="graphics-symbol"
                  aria-label={`${p.label}: ${metric.isCurrency ? formatCompactCurrency(value) : Math.round(value).toString()}`}
                />
                {/* Show value on top of bar (only if single metric to avoid clutter) */}
                {value > 0 && allGroupedMetrics.length === 1 && (
                  <text
                    x={barX + seriesBarWidth / 2}
                    y={barY - 4}
                    textAnchor="middle"
                    fontSize="9"
                    fill={metric.color}
                    fontWeight="600"
                    style={{ pointerEvents: 'none' }}
                  >
                    {metric.isCurrency ? formatCompactCurrency(value) : Math.round(value).toString()}
                  </text>
                )}
              </g>
            );
          })
        ))}
      </>
    );
  }

  // Standard mode
  const { currentTotals, comparisonTotals, hasComparison, maxValue } = props;

  return (
    <>
      {periods.map((p, i) => {
        const x = getX(i);
        const currentValue = currentTotals.get(p.key) || 0;
        const compValue = comparisonTotals.get(p.key) || 0;
        const currentHeight = Math.max(0, (currentValue / maxValue) * chartHeight);
        const compHeight = Math.max(0, (compValue / maxValue) * chartHeight);
        const currentBarX = hasComparison ? x + 2 : x - barWidth / 2;
        const currentBarY = barBottom - currentHeight;

        const handleMouseEnter = () => {
          onHover?.({
            x: hasComparison ? x : currentBarX + barWidth / 2,
            y: Math.min(currentBarY, barBottom - compHeight) - 8,
            periodKey: p.key,
            periodLabel: p.label,
            currentValue,
            comparisonValue: hasComparison ? compValue : undefined,
            isCurrency: true,
          });
        };

        const handleMouseLeave = () => {
          onHover?.(null);
        };

        return (
          <g key={p.key}>
            {/* Comparison bar (left) */}
            {hasComparison && (
              <rect
                x={x - barWidth - 2}
                y={barBottom - compHeight}
                width={barWidth}
                height={compHeight}
                fill={COLORS.COMPARISON}
                opacity="0.6"
                rx="3"
                style={{ cursor: 'pointer' }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            )}
            {/* Current bar (right or center) */}
            <rect
              x={currentBarX}
              y={currentBarY}
              width={barWidth}
              height={currentHeight}
              fill={COLORS.CURRENT}
              rx="3"
              style={{ cursor: 'pointer' }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onFocus={handleMouseEnter}
              onBlur={handleMouseLeave}
              tabIndex={0}
              role="graphics-symbol"
              aria-label={`${p.label}: ${formatCompactCurrency(currentValue)}${hasComparison && compValue > 0 ? ` (${currentValue >= compValue ? '+' : ''}${((currentValue - compValue) / compValue * 100).toFixed(0)}%)` : ''}`}
            />
            {/* Delta indicator */}
            {hasComparison && compValue > 0 && (
              <text
                x={x}
                y={Math.min(currentBarY, barBottom - compHeight) - 6}
                textAnchor="middle"
                fontSize="10"
                fill={currentValue >= compValue ? COLORS.POSITIVE : COLORS.NEGATIVE}
                fontWeight="600"
                style={{ pointerEvents: 'none' }}
              >
                {currentValue >= compValue ? '+' : ''}{((currentValue - compValue) / compValue * 100).toFixed(0)}%
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}
